import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search, X, ChevronDown, ChevronUp,
  CheckCircle, XCircle, Clock, Loader2, AlertTriangle,
  Star, RefreshCw, ShieldCheck, ShoppingCart,
  Ban, CheckCheck, Zap, Eye, Download, CheckSquare, Square,
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/Admin/Layout/AdminLayout';
import OrderDetailPanel from '../../components/Admin/SalesOverview/OrderDetailPanel';
import StatusBadge from '../../components/Admin/SalesOverview/StatusBadge';
import apiClient from '../../services/api/client';
import { ENDPOINTS } from '../../services/api/endpoints';
import { useSeller } from '../../context/SellerContext';
import MarketplaceSelector from '../../components/shared/MarketplaceSelector';

// ─── Constants ───────────────────────────────────────────────────────────────

const REVIEW_STATUS = {
  NOT_CHECKED: { label: 'Not Checked', cls: 'bg-slate-100 text-slate-600' },
  PENDING: { label: 'Pending', cls: 'bg-amber-100 text-amber-700' },
  ELIGIBLE: { label: 'Eligible', cls: 'bg-emerald-100 text-emerald-700' },
  SENT: { label: 'Sent', cls: 'bg-[#FFE8C8] text-[#C45500]' },
  ALREADY_SENT: { label: 'Already Sent', cls: 'bg-violet-100 text-violet-700' },
  NOT_ELIGIBLE: { label: 'Not Eligible', cls: 'bg-slate-100 text-slate-500' },
  EXPIRED: { label: 'Expired', cls: 'bg-slate-100 text-slate-400' },
  ERROR: { label: 'Error', cls: 'bg-red-100 text-red-700' },
};

const ORDER_STATUSES = ['Shipped', 'Delivered', 'PartiallyShipped'];

const DELIVERY_WINDOWS = [
  { value: '', label: 'All' },
  { value: '5', label: '5+ days ago' },
  { value: '7', label: '7+ days ago' },
  { value: '14', label: '14+ days ago' },
  { value: '30', label: '30+ days ago' },
];

const REVIEW_STATUSES = [
  { value: '', label: 'All Review Status' },
  { value: 'NOT_CHECKED', label: 'Not Checked' },
  { value: 'ELIGIBLE', label: 'Eligible' },
  { value: 'SENT', label: 'Sent' },
  { value: 'NOT_ELIGIBLE', label: 'Not Eligible' },
  { value: 'ERROR', label: 'Error' },
];

const ACTION_FILTERS = [
  { value: '', label: 'All Actions' },
  { value: 'CAN_CHECK', label: 'Can Check' },
  { value: 'CAN_SEND', label: 'Can Send' },
  { value: 'ALREADY_ACTIONED', label: 'Already Sent' },
];

const formatINR = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function getDaysAgo(dateStr) {
  if (!dateStr) return null;
  return Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24));
}

function ReviewStatusBadge({ status }) {
  const rs = REVIEW_STATUS[status] || REVIEW_STATUS.NOT_CHECKED;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${rs.cls}`}>{rs.label}</span>;
}

// ═════════════════════════════════════════════════════════════════════════════

export default function FeedbackRequestPage() {
  const { selectedSeller: sellerCtx } = useSeller();
  const sellerId = sellerCtx?.id || '';
  const sellerChannels = useMemo(() => (sellerCtx?.channels || []).filter(c => c.accountStatus === 'AUTHORIZED'), [sellerCtx]);
  const [selectedAccountId, setSelectedAccountId] = useState('');

  // Data
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sort
  const [sortBy, setSortBy] = useState('orderDate');
  const [sortDir, setSortDir] = useState('desc');

  // Row-level filters (only affect table rows, NOT stats)
  const [filterReviewStatus, setFilterReviewStatus] = useState('');
  const [filterOrderStatus, setFilterOrderStatus] = useState('');
  const [filterDeliveryDays, setFilterDeliveryDays] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [search, setSearch] = useState('');

  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Processing
  const [checkingIds, setCheckingIds] = useState(new Set());
  const [processingIds, setProcessingIds] = useState(new Set());
  const [bulkChecking, setBulkChecking] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);

  // Detail panel
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);

  // Load orders — only seller filter goes to API
  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (sellerId) params.append('sellerId', sellerId);
      const queryStr = params.toString();
      const url = `${ENDPOINTS.REVIEW_REQUESTS.GET_ALL}${queryStr ? `?${queryStr}` : ''}`;
      const res = await apiClient.get(url);
      const data = res.data || res;
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // ─── Stats (from ALL orders, only seller/platform filtered) ────────────
  const stats = useMemo(() => {
    const total = orders.length;
    const notChecked = orders.filter(o => o.requestStatus === 'NOT_CHECKED').length;
    const eligible = orders.filter(o => o.requestStatus === 'ELIGIBLE').length;
    const sent = orders.filter(o => o.requestStatus === 'SENT' || o.requestStatus === 'ALREADY_SENT').length;
    const notEligible = orders.filter(o => o.requestStatus === 'NOT_ELIGIBLE' || o.requestStatus === 'EXPIRED').length;
    const errors = orders.filter(o => o.requestStatus === 'ERROR').length;
    return { total, notChecked, eligible, sent, notEligible, errors };
  }, [orders]);

  // ─── Filtered rows (all client-side filters applied) ───────────────────
  const filtered = useMemo(() => {
    let result = orders;

    if (filterReviewStatus) result = result.filter(o => o.requestStatus === filterReviewStatus);
    if (filterOrderStatus) result = result.filter(o => o.orderStatus === filterOrderStatus);

    if (filterDeliveryDays) {
      const minDays = parseInt(filterDeliveryDays);
      result = result.filter(o => {
        const d = getDaysAgo(o.deliveredDate);
        return d !== null && d >= minDays;
      });
    }

    if (filterAction === 'CAN_CHECK') {
      result = result.filter(o => ['NOT_CHECKED', 'PENDING', 'NOT_ELIGIBLE', 'ERROR'].includes(o.requestStatus));
    } else if (filterAction === 'CAN_SEND') {
      result = result.filter(o => o.requestStatus === 'ELIGIBLE');
    } else if (filterAction === 'ALREADY_ACTIONED') {
      result = result.filter(o => o.requestStatus === 'SENT' || o.requestStatus === 'ALREADY_SENT');
    }

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(o =>
        o.amazonOrderId?.toLowerCase().includes(q) ||
        o.buyerCity?.toLowerCase().includes(q) ||
        o.sellerName?.toLowerCase().includes(q)
      );
    }

    // Sort
    result = [...result].sort((a, b) => {
      let va = a[sortBy], vb = b[sortBy];
      if (va == null) return 1; if (vb == null) return -1;
      if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb || '').toLowerCase(); }
      if (typeof va === 'number' || !isNaN(Number(va))) { va = Number(va); vb = Number(vb); }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [orders, filterReviewStatus, filterOrderStatus, filterDeliveryDays, filterAction, search, sortBy, sortDir]);

  const hasRowFilters = filterReviewStatus || filterOrderStatus || filterDeliveryDays || filterAction || search;

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ChevronDown size={14} className="text-gray-300" />;
    return sortDir === 'asc' ? <ChevronUp size={14} className="text-[#C45500]" /> : <ChevronDown size={14} className="text-[#C45500]" />;
  };

  const clearRowFilters = () => {
    setFilterReviewStatus(''); setFilterOrderStatus(''); setFilterDeliveryDays(''); setFilterAction(''); setSearch('');
  };

  // ─── Selection ─────────────────────────────────────────────────────────
  const checkableInView = useMemo(() => filtered.filter(o => ['NOT_CHECKED', 'PENDING', 'NOT_ELIGIBLE', 'ERROR'].includes(o.requestStatus)), [filtered]);
  const sendableInView = useMemo(() => filtered.filter(o => o.requestStatus === 'ELIGIBLE'), [filtered]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(o => o.id)));
    }
  };

  const selectedCheckable = useMemo(() => {
    return filtered.filter(o => selectedIds.has(o.id) && ['NOT_CHECKED', 'PENDING', 'NOT_ELIGIBLE', 'ERROR'].includes(o.requestStatus));
  }, [filtered, selectedIds]);

  const selectedSendable = useMemo(() => {
    return filtered.filter(o => selectedIds.has(o.id) && o.requestStatus === 'ELIGIBLE');
  }, [filtered, selectedIds]);

  // ─── Actions ───────────────────────────────────────────────────────────
  const handleCheck = useCallback(async (orderId) => {
    setCheckingIds(prev => new Set([...prev, orderId]));
    try {
      const res = await apiClient.post(ENDPOINTS.FEEDBACK_REQUESTS.CHECK_SINGLE(orderId));
      const data = res.data || res;
      setOrders(prev => prev.map(o =>
        o.id === orderId ? { ...o, requestStatus: data.status || o.requestStatus, reviewRequestId: data.reviewRequestId || o.reviewRequestId, eligibilityCheckedAt: data.eligibilityCheckedAt, notEligibleReason: data.status === 'NOT_ELIGIBLE' ? data.message : null, errorMessage: data.status === 'ERROR' ? data.message : null } : o
      ));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Check failed');
    } finally {
      setCheckingIds(prev => { const n = new Set(prev); n.delete(orderId); return n; });
    }
  }, []);

  const handleSend = useCallback(async (orderId) => {
    setProcessingIds(prev => new Set([...prev, orderId]));
    try {
      const res = await apiClient.post(ENDPOINTS.FEEDBACK_REQUESTS.SEND(orderId));
      const data = res.data || res;
      setOrders(prev => prev.map(o =>
        o.id === orderId ? { ...o, requestStatus: data.status || 'SENT', sentAt: data.sentAt } : o
      ));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Send failed');
    } finally {
      setProcessingIds(prev => { const n = new Set(prev); n.delete(orderId); return n; });
    }
  }, []);

  const handleBulkCheck = useCallback(async () => {
    const ids = selectedCheckable.map(o => o.id);
    if (ids.length === 0) { toast.error('No checkable orders selected'); return; }
    setBulkChecking(true);
    setCheckingIds(new Set(ids));
    let checked = 0, eligible = 0;
    for (const id of ids) {
      try {
        const res = await apiClient.post(ENDPOINTS.FEEDBACK_REQUESTS.CHECK_SINGLE(id));
        const data = res.data || res;
        setOrders(prev => prev.map(o =>
          o.id === id ? { ...o, requestStatus: data.status || o.requestStatus, reviewRequestId: data.reviewRequestId, eligibilityCheckedAt: data.eligibilityCheckedAt, notEligibleReason: data.status === 'NOT_ELIGIBLE' ? data.message : null } : o
        ));
        checked++;
        if (data.status === 'ELIGIBLE') eligible++;
      } catch { /* continue */ }
    }
    toast.success(`Checked ${checked} orders — ${eligible} eligible`);
    setBulkChecking(false);
    setCheckingIds(new Set());
    setSelectedIds(new Set());
  }, [selectedCheckable]);

  const handleBulkSend = useCallback(async () => {
    const ids = selectedSendable.map(o => o.id);
    if (ids.length === 0) { toast.error('No sendable orders selected'); return; }
    setBulkSending(true);
    setProcessingIds(new Set(ids));
    try {
      const res = await apiClient.post(ENDPOINTS.FEEDBACK_REQUESTS.BULK_SEND, { orderIds: ids });
      const data = res.data || res;
      const results = data.results || [];
      setOrders(prev => prev.map(o => {
        const m = results.find(r => r.id === o.id);
        return m ? { ...o, requestStatus: m.status || o.requestStatus, sentAt: m.sentAt || o.sentAt } : o;
      }));
      toast.success(`${data.sent || 0} review request${(data.sent || 0) > 1 ? 's' : ''} sent`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Bulk send failed');
    } finally {
      setBulkSending(false);
      setProcessingIds(new Set());
      setSelectedIds(new Set());
    }
  }, [selectedSendable]);

  // View detail
  const handleViewDetail = async (order) => {
    setSelectedOrder(order);
    setShowDetail(true);
    if (!order.orderItems) {
      setLoadingItems(true);
      try {
        const res = await apiClient.get(ENDPOINTS.ORDERS.GET_ITEMS(order.id));
        const data = res.data || res;
        if (data.orderItems) {
          const updated = { ...order, orderItems: typeof data.orderItems === 'string' ? data.orderItems : JSON.stringify(data.orderItems) };
          setSelectedOrder(updated);
          setOrders(prev => prev.map(o => o.id === order.id ? updated : o));
        }
      } catch { /* silent */ } finally { setLoadingItems(false); }
    }
  };

  const selectCls = "px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]";

  return (
    <AdminLayout>
      <div className="p-3 sm:p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Review Requests</h1>
            <p className="text-xs text-slate-400 mt-0.5">Request product reviews for delivered orders via Amazon Solicitations API</p>
          </div>
          <div className="flex items-center gap-2">
            <MarketplaceSelector value={selectedAccountId} onChange={setSelectedAccountId} />

            <div className="w-px h-8 bg-gray-200" />

            <button onClick={loadOrders} disabled={loading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50" title="Refresh">
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Stats badges — NOT affected by row filters */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            { label: 'Total', value: stats.total, key: '' },
            { label: 'Not Checked', value: stats.notChecked, key: 'NOT_CHECKED' },
            { label: 'Eligible', value: stats.eligible, key: 'ELIGIBLE' },
            { label: 'Sent', value: stats.sent, key: 'SENT' },
            { label: 'Not Eligible', value: stats.notEligible, key: 'NOT_ELIGIBLE' },
            { label: 'Errors', value: stats.errors, key: 'ERROR' },
          ].map(s => (
            <button key={s.label} onClick={() => setFilterReviewStatus(filterReviewStatus === s.key ? '' : s.key)}
              className={`bg-white rounded-xl shadow-sm p-4 text-left transition-all ${filterReviewStatus === s.key ? 'ring-2 ring-[#f08804]' : 'hover:shadow-md'}`}>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{s.label}</p>
              <p className="text-xl font-bold text-gray-800 mt-0.5">{s.value}</p>
            </button>
          ))}
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 max-w-xs">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search order ID, city, seller..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]" />
              {search && <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"><X size={14} /></button>}
            </div>

            {/* Order Status */}
            <select value={filterOrderStatus} onChange={(e) => setFilterOrderStatus(e.target.value)} className={selectCls}>
              <option value="">All Order Status</option>
              {ORDER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>

            {/* Delivery window */}
            <select value={filterDeliveryDays} onChange={(e) => setFilterDeliveryDays(e.target.value)} className={selectCls}>
              <option value="">Delivered: All</option>
              {DELIVERY_WINDOWS.filter(d => d.value).map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>

            {/* Review Status */}
            <select value={filterReviewStatus} onChange={(e) => setFilterReviewStatus(e.target.value)} className={selectCls}>
              {REVIEW_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>

            {/* Action filter */}
            <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)} className={selectCls}>
              {ACTION_FILTERS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>

            {hasRowFilters && (
              <button onClick={clearRowFilters} className="inline-flex items-center px-3 py-2 text-sm text-red-600 hover:text-red-800">
                <X size={14} className="mr-1" /> Clear
              </button>
            )}
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="bg-[#FFF4E6] border border-[#FFD89E] rounded-xl px-4 py-3 flex items-center gap-3">
            <span className="text-sm font-medium text-[#C45500]">{selectedIds.size} selected</span>
            <div className="w-px h-5 bg-[#FFD89E]" />
            {selectedCheckable.length > 0 && (
              <button onClick={handleBulkCheck} disabled={bulkChecking}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#C45500] bg-white border border-[#FFD89E] rounded-lg hover:bg-[#FFE8C8] disabled:opacity-50">
                {bulkChecking ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
                Check Eligibility ({selectedCheckable.length})
              </button>
            )}
            {selectedSendable.length > 0 && (
              <button onClick={handleBulkSend} disabled={bulkSending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-[#232F3E] rounded-lg hover:bg-[#37475A] disabled:opacity-50 shadow-sm">
                {bulkSending ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                Send Request ({selectedSendable.length})
              </button>
            )}
            <button onClick={() => setSelectedIds(new Set())} className="ml-auto text-xs font-medium text-[#C45500] hover:text-[#232F3E]">
              Clear selection
            </button>
          </div>
        )}

        {/* Results info */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">{filtered.length} of {orders.length} orders</span>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 w-[40px]">
                    <button onClick={toggleSelectAll} className="text-gray-400 hover:text-[#C45500]">
                      {selectedIds.size > 0 && selectedIds.size === filtered.length ? <CheckSquare size={16} /> : <Square size={16} />}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('amazonOrderId')}>
                    <div className="flex items-center gap-1">Order ID <SortIcon field="amazonOrderId" /></div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('orderDate')}>
                    <div className="flex items-center gap-1">Purchased <SortIcon field="orderDate" /></div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('deliveredDate')}>
                    <div className="flex items-center gap-1">Est. Delivery <SortIcon field="deliveredDate" /></div>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('orderTotal')}>
                    <div className="flex items-center justify-end gap-1">Total <SortIcon field="orderTotal" /></div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('orderStatus')}>
                    <div className="flex items-center gap-1">Order <SortIcon field="orderStatus" /></div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('requestStatus')}>
                    <div className="flex items-center gap-1">Review <SortIcon field="requestStatus" /></div>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>{Array.from({ length: 9 }).map((_, j) => (
                      <td key={j} className="px-4 py-4"><div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: `${50 + Math.random() * 50}%` }} /></td>
                    ))}</tr>
                  ))
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-16 text-center">
                      <Star size={48} className="mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500 text-lg font-medium">No orders found</p>
                      <p className="text-gray-400 text-sm mt-1">
                        {hasRowFilters ? 'Try adjusting your filters.' : 'Sync orders in Orders & Sales first.'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  filtered.map((o) => {
                    const purchaseDays = getDaysAgo(o.orderDate);
                    const deliveryDays = getDaysAgo(o.deliveredDate);
                    const deliveryPassed = deliveryDays !== null && deliveryDays >= 0;
                    const canCheck = ['NOT_CHECKED', 'PENDING', 'NOT_ELIGIBLE', 'ERROR'].includes(o.requestStatus);
                    const canSend = o.requestStatus === 'ELIGIBLE';
                    const isSent = o.requestStatus === 'SENT' || o.requestStatus === 'ALREADY_SENT';
                    const isChecking = checkingIds.has(o.id);
                    const isProcessing = processingIds.has(o.id);
                    const isSelected = selectedIds.has(o.id);

                    return (
                      <tr key={o.id} className={`hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-[#FFF9EE]' : ''}`} onClick={() => handleViewDetail(o)}>
                        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => toggleSelect(o.id)} className={`${isSelected ? 'text-[#C45500]' : 'text-gray-300 hover:text-gray-500'}`}>
                            {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-[#C45500]">{o.amazonOrderId}</p>
                          {o.sellerName && <p className="text-xs text-gray-400">{o.sellerName}</p>}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-700">{formatDate(o.orderDate)}</p>
                          {purchaseDays !== null && <p className="text-xs text-gray-400">{purchaseDays}d ago</p>}
                        </td>
                        <td className="px-4 py-3">
                          {o.deliveredDate ? (
                            <>
                              <p className="text-sm text-gray-700">{formatDate(o.deliveredDate)}</p>
                              {deliveryDays !== null && deliveryDays < 0 ? (
                                <p className="text-[10px] text-[#f08804] font-medium">in {Math.abs(deliveryDays)}d</p>
                              ) : deliveryDays !== null && deliveryDays >= 5 ? (
                                <p className="text-[10px] text-emerald-600 font-medium">{deliveryDays}d ago</p>
                              ) : deliveryDays !== null ? (
                                <p className="text-[10px] text-amber-600 font-medium">{deliveryDays}d ago (too early)</p>
                              ) : null}
                            </>
                          ) : <span className="text-xs text-gray-300">-</span>}
                        </td>
                        <td className="px-4 py-3 text-center text-sm">
                          {o.orderItems ? (
                            <span className="text-gray-700 font-medium">
                              {(() => { try { const p = typeof o.orderItems === 'string' ? JSON.parse(o.orderItems) : o.orderItems; return Array.isArray(p) ? p.length : '-'; } catch { return '-'; } })()}
                            </span>
                          ) : (
                            <button onClick={(e) => { e.stopPropagation(); handleViewDetail(o); }}
                              className="inline-flex items-center gap-1 text-[#f08804] hover:text-[#C45500]" title="Load items">
                              <Download size={13} /><span className="text-xs font-medium">Load</span>
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-gray-800">
                          {o.orderTotal ? formatINR(parseFloat(o.orderTotal)) : '-'}
                        </td>
                        <td className="px-4 py-3"><StatusBadge status={o.orderStatus} /></td>
                        <td className="px-4 py-3"><ReviewStatusBadge status={o.requestStatus} /></td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-1.5">
                            {canCheck && (
                              <button onClick={() => handleCheck(o.id)} disabled={isChecking}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                                {isChecking ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />} Check
                              </button>
                            )}
                            {canSend && (
                              <button onClick={() => handleSend(o.id)} disabled={isProcessing}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-white bg-[#232F3E] rounded-lg hover:bg-[#37475A] disabled:opacity-50 shadow-sm">
                                {isProcessing ? <Loader2 size={11} className="animate-spin" /> : <Star size={11} />} Send
                              </button>
                            )}
                            {isSent && <span className="text-[10px] text-[#f08804] font-medium">Sent</span>}
                            <button onClick={() => handleViewDetail(o)} className="text-gray-400 hover:text-[#C45500] p-1" title="View">
                              <Eye size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <OrderDetailPanel order={selectedOrder} isOpen={showDetail} loadingItems={loadingItems}
        onClose={() => { setShowDetail(false); setSelectedOrder(null); setLoadingItems(false); }} />
    </AdminLayout>
  );
}
